<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Check, ChevronsUpDown } from 'lucide-vue-next';

import useCountrySwitcher from '@/composables/useCountrySwitcher';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Button from '@/components/Button.vue';

type Country = {
  id: string;
  current?: boolean;
  displayName?: string;
  channelName?: string;
  availableLanguages: Array<{ languageId: string; name?: string; current?: boolean }>;
};

const emit = defineEmits<{ (e: 'change'): void }>();

const { availableCountries, changeCountry } = useCountrySwitcher();
const { websiteText } = useWebsiteTexts();

const PENDING_KEY = 'localeSelectorPending';

const selectedCountryId = ref('');
const selectedLanguageId = ref('');
const initialCountryId = ref('');
const initialLanguageId = ref('');
const submitting = ref(false);
const countryOpen = ref(false);
const countrySearch = ref('');

function readPendingSelection(): { countryId: string; languageId: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

watch(
  availableCountries,
  (countries) => {
    if (!countries?.length) return;

    // Prefer the selection the user just confirmed (persisted across the reload),
    // since the backend's `current` flag can be stale on the immediate next load.
    const pending = readPendingSelection();

    const current =
      (pending && (countries as Country[]).find((c) => c.id === pending.countryId)) ||
      (countries as Country[]).find((c) => c.current) ||
      (countries[0] as Country);
    selectedCountryId.value = current.id;
    initialCountryId.value = current.id;

    const langs = current.availableLanguages ?? [];
    const pendingLang =
      pending && langs.find((l: any) => l.languageId === pending.languageId);
    const currentLang = pendingLang || langs.find((l: any) => l.current) || langs[0];
    selectedLanguageId.value = currentLang?.languageId ?? '';
    initialLanguageId.value = currentLang?.languageId ?? '';
  },
  { immediate: true }
);

const selectedCountry = computed<Country | null>(() => {
  const list = availableCountries.value as Country[];
  return list.find((c) => c.id === selectedCountryId.value) || null;
});

const availableLanguages = computed(() => selectedCountry.value?.availableLanguages ?? []);

watch(selectedCountryId, () => {
  const langs = selectedCountry.value?.availableLanguages ?? [];
  const lang = langs.find((l) => l.current) || langs[0];
  selectedLanguageId.value = lang?.languageId ?? '';
});

const hasChanges = computed(() => selectedCountryId.value !== initialCountryId.value || selectedLanguageId.value !== initialLanguageId.value);

function countryName(c: Country) {
  return c.displayName || c.channelName || c.id;
}

function flagSrc(id: string) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    return require(`@/static/flags/${id.toLowerCase()}.svg`);
  } catch {
    return '';
  }
}

const filteredCountries = computed(() => {
  const list = availableCountries.value as Country[];
  const q = countrySearch.value.trim().toLowerCase();
  if (!q) return list;
  return list.filter((c) => countryName(c).toLowerCase().includes(q) || c.id.toLowerCase().includes(q));
});

function selectCountry(id: string) {
  selectedCountryId.value = id;
  countryOpen.value = false;
  countrySearch.value = '';
}

async function changeCountryWithRetry(countryId: string, languageId: string) {
  try {
    return await changeCountry(countryId, languageId);
  } catch {
    // The backend setCountry mutation rejects the first attempt when the
    // session/cart state isn't seeded yet; a single retry reliably succeeds.
    return await changeCountry(countryId, languageId);
  }
}

async function onConfirm() {
  if (!hasChanges.value || submitting.value) return;
  submitting.value = true;
  try {
    const redirectUrl = await changeCountryWithRetry(selectedCountryId.value, selectedLanguageId.value);
    emit('change');

    // Persist the chosen locale so the picker reflects it after the reload,
    // even if the freshly-fetched country-switcher data has a stale `current` flag.
    try {
      window.sessionStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          countryId: selectedCountryId.value,
          languageId: selectedLanguageId.value,
        }),
      );
    } catch {
      /* sessionStorage unavailable — non-fatal */
    }

    if (redirectUrl) {
      window.location.assign(redirectUrl);
    } else {
      window.location.reload();
    }
  } catch (err) {
    console.error('Failed to change country/language', err);
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div v-if="availableCountries.length >= 1" class="flex flex-col gap-4">
    <!-- Country -->
    <div class="flex flex-col gap-1.5">
      <label class="text-[11px] font-normal uppercase tracking-[0.1em] text-foreground/65">
        {{ websiteText('countryswitcher__country').value }}
      </label>
      <Popover v-model:open="countryOpen">
        <PopoverTrigger as-child>
          <button
            type="button"
            class="btn-bare flex h-10 w-full items-center justify-between rounded-md border border-border bg-background px-3 text-sm font-normal tracking-normal text-foreground transition-colors hover:bg-accent/50"
          >
            <span v-if="selectedCountry" class="flex items-center gap-2">
              <span v-if="flagSrc(selectedCountry.id)" class="inline-flex h-4 w-4 shrink-0 overflow-hidden rounded-full">
                <img :src="flagSrc(selectedCountry.id)" :alt="countryName(selectedCountry)" class="h-full w-full object-cover" />
              </span>
              <span>{{ countryName(selectedCountry) }}</span>
            </span>
            <span v-else class="text-muted-foreground">
              {{ websiteText('countryswitcher__country').value || 'Select country…' }}
            </span>
            <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" :side-offset="4" class="z-[10001] w-[var(--reka-popover-trigger-width)] border-border bg-background p-0 text-foreground">
          <div class="border-b border-border px-3 py-2">
            <input
              v-model="countrySearch"
              type="text"
              :placeholder="websiteText('countryswitcher__country').value || 'Search country…'"
              class="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div class="max-h-72 overflow-y-auto p-1">
            <button
              v-for="c in (filteredCountries as Country[])"
              :key="c.id"
              type="button"
              class="btn-bare flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm font-normal tracking-normal text-foreground hover:bg-accent"
              @click="selectCountry(c.id)"
            >
              <span v-if="flagSrc(c.id)" class="inline-flex h-4 w-4 shrink-0 overflow-hidden rounded-full">
                <img :src="flagSrc(c.id)" :alt="countryName(c)" class="h-full w-full object-cover" />
              </span>
              <span class="flex-1">{{ countryName(c) }}</span>
              <Check v-if="c.id === selectedCountryId" class="h-4 w-4" />
            </button>
            <p v-if="filteredCountries.length === 0" class="m-0 px-2 py-2 text-sm text-muted-foreground">
              {{ websiteText('countryswitcher__nocountry').value || 'No results' }}
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
    <div v-if="availableLanguages.length" class="flex flex-col gap-1.5">
      <label class="text-[11px] font-normal uppercase tracking-[0.1em] text-foreground/65">
        {{ websiteText('countryswitcher__language').value }}
      </label>
      <Select v-model="selectedLanguageId">
        <SelectTrigger class="h-10 rounded-sm border-border bg-background font-normal tracking-normal text-foreground transition-colors hover:bg-accent/50">
          <SelectValue :placeholder="websiteText('countryswitcher__language').value" />
        </SelectTrigger>
        <SelectContent class="z-[10001] border-border bg-background text-foreground">
          <SelectItem v-for="lang in availableLanguages" :key="lang.languageId" :value="lang.languageId" class="font-normal tracking-normal">
            {{ lang.name || lang.languageId }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <Button
      variant="primary"
      size="md"
      class="w-full rounded-sm disabled:bg-foreground disabled:text-background disabled:cursor-not-allowed"
      :state="!hasChanges ? 'disabled' : submitting ? 'pending' : 'idle'"
      @click="onConfirm"
    >
      {{ websiteText('countryswitcher__location').value || 'Change location' }}
    </Button>
  </div>
</template>
